import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, Upload, MapPin, Phone, ShieldCheck, X, Loader2, FileText, Download, CheckCircle2, AlertTriangle, Contact2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SOURCE_META = {
  kalpataru:          { label: "Kalpataru",         color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  yellow_pages:       { label: "Yellow Pages",      color: "bg-amber-100 text-amber-800 border-amber-200" },
  bank:               { label: "Bank",              color: "bg-blue-100 text-blue-800 border-blue-200" },
  school:             { label: "School",            color: "bg-purple-100 text-purple-800 border-purple-200" },
  customer_list:      { label: "Customer list",     color: "bg-[#E05E36]/10 text-[#E05E36] border-[#E05E36]/30" },
  vendor_list:        { label: "Vendor list",       color: "bg-teal-100 text-teal-800 border-teal-200" },
  personal_contacts:  { label: "Personal contacts", color: "bg-pink-100 text-pink-800 border-pink-200" },
  housing_society:    { label: "Housing society",   color: "bg-lime-100 text-lime-800 border-lime-200" },
  demo_seed:          { label: "Demo",              color: "bg-stone-100 text-stone-600 border-stone-200" },
  manual:             { label: "Manual",            color: "bg-stone-100 text-stone-700 border-stone-200" },
};

export default function Directory() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [source, setSource] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ cities: [], sources: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [initSeeded, setInitSeeded] = useState(false);

  // Load facets once; if the DB is empty, offer a one-tap demo seed.
  useEffect(() => {
    api.get("/directory/facets").then(r => {
      setFacets(r.data);
      if (r.data.total === 0) setInitSeeded(false);
    }).catch(() => {});
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const r = await api.get("/directory/search", { params: { q: q || undefined, city: city || undefined, source: source || undefined, limit: 60 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error("Search failed");
    } finally { setLoading(false); }
  };

  // Initial fetch
  useEffect(() => { search(); /* eslint-disable-line */ }, []);

  const runDemoSeed = async () => {
    try {
      const r = await api.post("/directory/seed-demo");
      toast.success(`Seeded ${r.data.inserted || r.data.count || 0} demo entries`);
      setInitSeeded(true);
      const f = await api.get("/directory/facets"); setFacets(f.data);
      await search();
    } catch (e) {
      toast.error("Seed failed — sign in and retry");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="directory-page">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3 justify-between mb-4">
        <div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900 flex items-center gap-2">
            <Contact2 className="text-[#E05E36]" /> Directory
          </h1>
          <p className="text-stone-500 text-sm">
            Every mobile number, one filterable list · {facets.total.toLocaleString("en-IN")} entries across {facets.cities.length} cities
          </p>
        </div>
        <div className="flex gap-2">
          {user && (
            <Button onClick={() => setShowUpload(true)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="directory-upload-btn">
              <Upload size={14} className="mr-1.5" /> Upload CSV
            </Button>
          )}
          {facets.total === 0 && user?.is_admin && (
            <Button onClick={runDemoSeed} variant="outline" className="rounded-full" data-testid="directory-seed-btn">
              <FileText size={14} className="mr-1.5" /> Seed 300 demo
            </Button>
          )}
        </div>
      </div>

      {/* Search + filters */}
      <div className="bg-white border border-stone-200 rounded-2xl p-3 md:p-4 flex flex-wrap gap-2 items-center" data-testid="directory-search-bar">
        <div className="flex-1 min-w-[220px] relative">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Name, mobile, or category…"
            className="pl-8 rounded-full"
            data-testid="directory-search-input"
          />
        </div>
        <Select value={city} onValueChange={(v) => { setCity(v); setTimeout(search, 50); }}>
          <SelectTrigger className="w-40 rounded-full" data-testid="directory-city-select">
            <SelectValue placeholder="All cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" onClick={() => setCity("")}>All cities</SelectItem>
            {facets.cities.map(c => <SelectItem key={c.value} value={c.value}>{c.value} ({c.count})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(v) => { setSource(v === "__all__" ? "" : v); setTimeout(search, 50); }}>
          <SelectTrigger className="w-40 rounded-full" data-testid="directory-source-select">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {facets.sources.map(s => (
              <SelectItem key={s.value} value={s.value}>
                {SOURCE_META[s.value]?.label || s.value} ({s.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={search} className="rounded-full bg-stone-900 hover:bg-stone-800" data-testid="directory-search-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Search"}
        </Button>
      </div>

      <div className="text-xs text-stone-500 mt-2">
        {loading ? "Searching…" : `${total.toLocaleString("en-IN")} matches`}
        {city && <> · in {city}</>} {source && <> · source: {SOURCE_META[source]?.label || source}</>}
      </div>

      {/* Results grid */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="directory-results">
        {items.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 text-stone-500">
            <Contact2 size={40} className="mx-auto text-stone-300 mb-2" />
            <div className="font-bold text-stone-700 mb-1">No entries found.</div>
            <div className="text-sm">
              {facets.total === 0
                ? "Upload a CSV or run the demo seed to start."
                : "Try clearing filters or a different city/source."}
            </div>
          </div>
        )}
        {items.map(e => (
          <EntryCard key={e.entry_id} e={e} />
        ))}
      </div>

      {/* Upload modal */}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); search(); api.get("/directory/facets").then(r => setFacets(r.data)); }} />}

      {/* Footer legal */}
      <div className="mt-10 text-center text-[11px] text-stone-400 max-w-md mx-auto leading-relaxed">
        Uploads require your explicit consent that you have permission to share these contacts.
        We do not scrape Google, IndiaMart, JustDial or WhatsApp — every entry has a source and an uploader trace.
      </div>
    </div>
  );
}

function EntryCard({ e }) {
  const meta = SOURCE_META[e.source] || SOURCE_META.manual;
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 transition-colors" data-testid={`directory-entry-${e.entry_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-stone-100 grid place-items-center text-stone-500 font-black shrink-0">
          {(e.name || "?")[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="font-bold text-stone-900 truncate">{e.name || "(no name)"}</div>
            {e.verified && <ShieldCheck size={12} className="text-emerald-600" />}
            {(e.claimed || e.claimed_by_user_id) && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-1.5">Claimed</span>
            )}
          </div>
          <div className="text-xs text-stone-500 font-mono truncate">{e.mobile_masked}</div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            {e.city && <span className="inline-flex items-center gap-1 text-stone-600"><MapPin size={10} /> {e.city}</span>}
            {e.category && <span className="text-stone-600">· {e.category}</span>}
            <span className={`ml-auto rounded-full border px-2 py-0.5 font-bold ${meta.color}`}>{meta.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadModal({ onClose, onDone }) {
  const [source, setSource] = useState("customer_list");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please confirm you have consent to upload these contacts.");
    if (!file) return toast.error("Pick a CSV file.");
    const form = new FormData();
    form.append("source", source);
    form.append("consent", "true");
    form.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/directory/import-csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${r.data.inserted} new · ${r.data.updated} updated · ${r.data.skipped_invalid} skipped`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center px-4" onClick={onClose} data-testid="upload-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="heading text-xl font-black">Upload directory CSV</h2>
            <p className="text-xs text-stone-500 mt-1">Expected columns: mobile, name, city, category, address</p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-stone-500">Source</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="upload-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_META).filter(([k]) => k !== "demo_seed").map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-stone-500">CSV file</label>
            <input
              type="file" accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-stone-100 hover:file:bg-stone-200"
              data-testid="upload-file-input"
            />
            {file && <div className="text-xs text-stone-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>

          <label className="flex items-start gap-2 text-xs text-stone-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="upload-consent" />
            <span>
              <b>I confirm I have permission</b> to share these contacts. Uploading contact lists without consent may violate India's DPDP-Act 2023.
            </span>
          </label>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={uploading || !consent || !file} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="upload-submit">
              {uploading ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Uploading…</> : <><Upload size={14} className="mr-1.5" /> Import</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
